import { useMatomo } from '@datapunt/matomo-tracker-react';
import { useQueryClient } from '@tanstack/react-query';
import cx from 'clsx';
import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Tooltip } from 'react-tooltip';

import { ReactComponent as MapIconSvg } from '@assets/svg/map-icon.svg';
import { ReactComponent as AiIcon } from '@assets/svg/nav-icons/ic-AI.svg';
import { ReactComponent as AdministrationIcon } from '@assets/svg/nav-icons/ic-administartion.svg';
import { ReactComponent as GridView } from '@assets/svg/nav-icons/ic-eqOverview.svg';
import { ReactComponent as EquipmentListPageIcon } from '@assets/svg/nav-icons/ic-equipmentList.svg';
import { ReactComponent as ExecutiveSummaryIcon } from '@assets/svg/nav-icons/ic-executiveSummary.svg';
import { ReactComponent as HideMenuIcon } from '@assets/svg/nav-icons/ic-expandMenu.svg';
import { ReactComponent as FacilityOverviewIcon } from '@assets/svg/nav-icons/ic-facilityOverview.svg';
import { ReactComponent as HeatMap } from '@assets/svg/nav-icons/ic-heatMap.svg';
import { ReactComponent as Help } from '@assets/svg/nav-icons/ic-help.svg';
import { ReactComponent as Logout } from '@assets/svg/nav-icons/ic-logout.svg';
import { ReactComponent as ManageFacilityPageIcon } from '@assets/svg/nav-icons/ic-manage-facility.svg';
import { ReactComponent as ManageLocationsPageIcon } from '@assets/svg/nav-icons/ic-manageLocations.svg';
import { ReactComponent as ManageTranslationsPageIcon } from '@assets/svg/nav-icons/ic-manageTranslations.svg';
import { ReactComponent as MultiplicationFactorsPageIcon } from '@assets/svg/nav-icons/ic-multiplicationFactors.svg';
import { ReactComponent as ProfIco } from '@assets/svg/nav-icons/ic-myProfile.svg';
import { ReactComponent as WhatNew } from '@assets/svg/nav-icons/ic-new.svg';
import { ReactComponent as Reports } from '@assets/svg/nav-icons/ic-reports.svg';
import { ReactComponent as Reload } from '@assets/svg/nav-icons/ic-switchCustomer.svg';
import { ReactComponent as Users } from '@assets/svg/nav-icons/ic-users.svg';

import { MODAL_NAMES } from '@constants/modalRenderers';

import useTranslation from '@hooks/useTranslation';

import { useWhatsNewCountQuery } from '@services/useWhatsNewCountQuery';
import { useUserProfileQuery } from '@services/user/useUserProfileQuery';

import { selectAppRtl } from '@store/useAppStore';
import {
  selectAppPersistOnBoardStart,
  selectAppPersistSidebarResponse,
  useAppPersistActions,
} from '@store/useAppStorePersist';
import { useModalActions } from '@store/useModalStore';

import Collapse from '../Collapse/Collapse';
import NavButtonItem from '../NavButtonItem/NavButtonItem';
import NavLinkItem from '../NavLinkItem/NavLinkItem';
import SidebarLogo from '../SidebarLogo/SidebarLogo';
import style from './style.module.scss';

const Sidebar = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const { setActiveModal } = useModalActions();
  const { locationId, customerId: paramsCustomerId } = useParams();
  const { data: profileData } = useUserProfileQuery();
  const { data: whatsNewCount } = useWhatsNewCountQuery();
  const { setAppPersistSidebarResponse } = useAppPersistActions();

  const sidebarNavRef = useRef<HTMLDivElement>(null);

  const onBoardStart = selectAppPersistOnBoardStart();
  const sidebarResponse = selectAppPersistSidebarResponse();
  const rtl = selectAppRtl();

  const [isOpenFacilityAdministrationCollapse, setIsOpenFacilityAdministrationCollapse] = useState(false);
  const [isOpenProfileCollapse, setIsOpenProfileCollapse] = useState(false);

  useEffect(() => {
    if (onBoardStart) {
      setIsOpenProfileCollapse(false);
      setIsOpenFacilityAdministrationCollapse(false);
    }
  }, [onBoardStart]);

  const customerId = paramsCustomerId || profileData?.user?.customer_id;
  const MAIN_ROUTE = `/customer/${customerId}`;
  const TOOLTIP_ID = 'sidebar_tooltip';

  const hideSidebar = () => {
    setAppPersistSidebarResponse(!sidebarResponse);
  };

  const openSwitchCustomer = () => {
    setActiveModal({
      name: MODAL_NAMES.switchCustomerModal,
    });
  };

  const openLogout = () => {
    setActiveModal({ name: MODAL_NAMES.logoutConfirmModal });
  };

  const reload = () => queryClient.invalidateQueries();

  return (
    <div
      className={cx(style.sidebar, {
        [style.hiddenSidebar]: sidebarResponse,
      })}
    >
      <SidebarLogo handleClick={reload} sidebarResponse={sidebarResponse} />
      <nav className={style.sidebarNav} ref={sidebarNavRef}>
        <div className={style.sidebarNavTopWrap}>
          <div className={style.sidebarNavTopContainer}>
            <div>
              <NavLinkItem
                id="tourStep-2"
                to={locationId ? `${MAIN_ROUTE}/location/${locationId}` : `${MAIN_ROUTE}`}
                tooltipId={TOOLTIP_ID}
                text={t('Action Items')}
                icon={<AiIcon />}
              />
              <NavLinkItem
                to={`${MAIN_ROUTE}/executive-summary`}
                tooltipId={TOOLTIP_ID}
                text={t('Executive Summary')}
                icon={<ExecutiveSummaryIcon style={{ height: 19 }} />}
              />
              <NavLinkItem
                id="tourStep-19"
                to={locationId ? `${MAIN_ROUTE}/sensors/location/${locationId}` : `${MAIN_ROUTE}/sensors`}
                text={t('Asset Overview')}
                icon={<GridView />}
                tooltipId={TOOLTIP_ID}
              />
            </div>
            <NavLinkItem
              to={`${MAIN_ROUTE}/hardware-overview`}
              tooltipId={TOOLTIP_ID}
              text={t('Hardware Overview')}
              icon={<FacilityOverviewIcon />}
            />
            <NavLinkItem
              id="tourStep-20"
              to={`${MAIN_ROUTE}/reports`}
              tooltipId={TOOLTIP_ID}
              text={t('Downloads & Reports')}
              icon={<Reports />}
            />
            <NavLinkItem
              to={`${MAIN_ROUTE}/users`}
              tooltipId={TOOLTIP_ID}
              text={t('Users')}
              icon={<Users />}
            />

            {(profileData?.userAccess as any).manageGpsTracking && //TODO: Remove 'any' once profileData and userAccess are properly typed
              !!profileData?.customer?.has_gps_tracking_access && (
                <NavLinkItem
                  to={`${MAIN_ROUTE}/gps-asset-tracking`}
                  tooltipId={TOOLTIP_ID}
                  text={t('GPS Asset Tracking')}
                  icon={<MapIconSvg className={style.mapIconSvg} />}
                />
              )}

            {profileData?.customer.customer_id === 502 && (
              <NavLinkItem
                to={`${MAIN_ROUTE}/sensors-temperature`}
                tooltipId={TOOLTIP_ID}
                text={t('Heat Map')}
                icon={<HeatMap />}
              />
            )}
          </div>
          {(profileData?.userAccess as any).manageEquipmentDeactivation! && ( //TODO: Remove 'any' once profileData is properly typed
            <Collapse
              leftContent={
                <div className={style.facilityAdministrationBlock}>
                  <NavButtonItem
                    tooltipId={TOOLTIP_ID}
                    text={t('Facility Administration')}
                    icon={<AdministrationIcon />}
                  />
                </div>
              }
              isExpanded={isOpenFacilityAdministrationCollapse}
              onToggle={setIsOpenFacilityAdministrationCollapse}
            >
              <div className={style.collapseWrap}>
                {!IS_PROD && ( // TODO: Remove once "Manage Facility Group" Phase 2 is completed.
                  <NavLinkItem
                    to={`${MAIN_ROUTE}/manage-facility`}
                    tooltipId={TOOLTIP_ID}
                    text={t('Manage Facility Group')}
                    icon={<ManageFacilityPageIcon className={style.manageFacilityIcon} />}
                  />
                )}
                <NavLinkItem
                  to={
                    locationId
                      ? `${MAIN_ROUTE}/manage-equipment/location/${locationId}`
                      : `${MAIN_ROUTE}/manage-equipment`
                  }
                  tooltipId={TOOLTIP_ID}
                  text={t('Manage Equipment')}
                  icon={<EquipmentListPageIcon />}
                />
                <NavLinkItem
                  to={
                    locationId
                      ? `${MAIN_ROUTE}/manage-locations/location/${locationId}`
                      : `${MAIN_ROUTE}/manage-locations`
                  }
                  tooltipId={TOOLTIP_ID}
                  text={t('Manage Locations')}
                  icon={<ManageLocationsPageIcon />}
                />
                {(profileData?.userAccess as any).manageEquipmentDeactivation && ( //TODO: Remove 'any' once profileData and userAccess are properly typed
                  <NavLinkItem
                    to={`${MAIN_ROUTE}/multiplication-factors`}
                    tooltipId={TOOLTIP_ID}
                    text={t('Multiplication factors')}
                    icon={<MultiplicationFactorsPageIcon />}
                  />
                )}
                <NavLinkItem
                  to={
                    locationId
                      ? `${MAIN_ROUTE}/manage-translations/location/${locationId}`
                      : `${MAIN_ROUTE}/manage-translations`
                  }
                  tooltipId={TOOLTIP_ID}
                  text={t('Manage Translation')}
                  icon={<ManageTranslationsPageIcon />}
                />
              </div>
            </Collapse>
          )}
        </div>
        <div className={style.sidebarNavBottomWrap}>
          <div
            className={cx(style.sidebarNavBottomContainer, {
              [style.closedSidebarNavBottomContainer]: sidebarResponse,
            })}
          >
            <div className={cx({ [style.hasNewMessage]: whatsNewCount })}>
              <NavLinkItem
                to={`${MAIN_ROUTE}/whats-new`}
                tooltipId={TOOLTIP_ID}
                text={t("What's New")}
                icon={<WhatNew />}
              />
            </div>
            <NavLinkItem
              to={`${MAIN_ROUTE}/help`}
              tooltipId={TOOLTIP_ID}
              text={t('Help and Resources')}
              icon={<Help />}
            />
            <NavButtonItem
              tooltipId={TOOLTIP_ID}
              text={t('Show Menu')}
              icon={
                <HideMenuIcon
                  className={cx({
                    [style.showMenuIcon]: !sidebarResponse,
                  })}
                />
              }
              onClick={hideSidebar}
              className={cx(style.hideMenuNavItem, {
                [style.closedHideMenuNavItem]: sidebarResponse,
              })}
            >
              {t('Hide menu')}
            </NavButtonItem>
          </div>
          {!!profileData?.user && (
            <Collapse
              leftContent={
                <div className={style.userProfileBlock}>
                  <NavButtonItem
                    tooltipId={TOOLTIP_ID}
                    text={profileData?.user?.full_name || ''}
                    icon={
                      <div className={style.userImage}>
                        <img src={profileData?.user?.userpic} alt="profile" />
                      </div>
                    }
                    className={style.userProfileNavItem}
                  />
                </div>
              }
              isExpanded={isOpenProfileCollapse}
              onToggle={setIsOpenProfileCollapse}
              parentWrapRef={sidebarNavRef}
              variant="filled"
            >
              <div className={style.collapseWrap}>
                <NavLinkItem
                  to={`${MAIN_ROUTE}/profile`}
                  tooltipId={TOOLTIP_ID}
                  text={t('My Profile')}
                  icon={<ProfIco />}
                />
                {profileData?.user?.fullServiceCustomersCount > 1 && (
                  <NavButtonItem
                    tooltipId={TOOLTIP_ID}
                    text={t('Switch Customer')}
                    icon={<Reload />}
                    onClick={openSwitchCustomer}
                  />
                )}
                <NavButtonItem
                  tooltipId={TOOLTIP_ID}
                  text={t('Log Out')}
                  icon={<Logout />}
                  onClick={openLogout}
                />
              </div>
            </Collapse>
          )}
        </div>
        {sidebarResponse && (
          <Tooltip id={TOOLTIP_ID} place={rtl ? 'left' : 'right'} style={{ position: 'fixed' }} />
        )}
      </nav>
    </div>
  );
};

export default Sidebar;
